import React, { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import './MentorshipForm.css';

console.log("🔥 MentorshipForm loaded");

// read images from public/ to avoid bundler import issues
const fwilLogoPath = '/fw_logo.jpg';
const heroImagePath = '/hero_african_lawyer.jpg';

// Supabase client (reads from env) - accept REACT_APP_ or VITE_ prefixed envs
const SUPA_URL = process.env.REACT_APP_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const SUPA_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';
let supabase;
try {
  supabase = createClient(SUPA_URL, SUPA_KEY);
  console.log('Supabase init -> url:', SUPA_URL ? '(present)' : '(missing)', ' key:', SUPA_KEY ? '(present)' : '(missing)');
} catch (e) {
  console.error('Supabase client error', e);
  supabase = {
    from: () => ({ insert: async () => ({ error: new Error('Supabase client missing') }) })
  };
}

// PayFast config from env
const PAYFAST_URL = process.env.REACT_APP_PAYFAST_URL || process.env.VITE_PAYFAST_URL || 'https://www.payfast.co.za/eng/process';
const PAYFAST_MERCHANT_ID = process.env.REACT_APP_PAYFAST_MERCHANT_ID || process.env.VITE_PAYFAST_MERCHANT_ID || '';
const PAYFAST_MERCHANT_KEY = process.env.REACT_APP_PAYFAST_MERCHANT_KEY || process.env.VITE_PAYFAST_MERCHANT_KEY || '';

// put near your PayFast config at the top
const APP_BASE_URL =
  process.env.REACT_APP_APP_BASE_URL ||
  process.env.VITE_APP_BASE_URL ||
  'https://fwil-mentorship.vercel.app';

const PAYFAST_RETURN_URL =
  process.env.REACT_APP_PAYFAST_RETURN_URL ||
  process.env.VITE_PAYFAST_RETURN_URL ||
  `${APP_BASE_URL}/?pay=success`;

const PAYFAST_CANCEL_URL =
  process.env.REACT_APP_PAYFAST_CANCEL_URL ||
  process.env.VITE_PAYFAST_CANCEL_URL ||
  `${APP_BASE_URL}/?pay=cancel`;

// fixed amount
const DEFAULT_PAYFAST_AMOUNT = '350';
const PROGRAMMES = ['FWiL (SA)', 'FWiL (Botswana)', 'FMiL (SA)'];
const programmeNote = (name) => `Programme: ${name}`;

// Explicitly reference to satisfy CI / ESLint
void PAYFAST_RETURN_URL;
void PAYFAST_CANCEL_URL;


const DEFAULT_MENTORSHIP_CONTENT = {
  header: {
    logoUrl: fwilLogoPath,
  },
  hero: {
    label: 'FWIL Mentorship & Career Coaching 2026',
    heading: 'Intake 4 (Sep - Oct)',
    description:
      'Exclusive to law students that are enrolled in a tertiary institution and graduates entering the profession within South Africa. The 8 week online programme includes sessions during which the mentees are exposed to traditional and non-traditional career opportunities available within legal profession, and receive training in the following areas: CV/Cover Letter Drafting | Personal Branding | Mental health education | Professional Conduct | Articles, Pupillage & Alternatives | Interview prep: What recruiters want.',
    note: 'Hybrid option is only available for PTA & JHB',
    heroImage: heroImagePath,
    badge: 'MENTORSHIP & CAREER INSIGHT',
    caption:
      'A glimpse of the FWIL community in action — inspiring and supporting one another.',
  },
  buttons: {
    onlineLabel: 'Online',
    onlineAmount: '350',
    hybridLabel: 'Hybrid',
    hybridAmount: '550',
  },
  form: {
    modalTitle: 'Tell us about you',
    paymentNote:
      'NB: Please note that after paying the fee, you will be fully registered.',
    continuePrefix: 'Continue: once off fee',
  },
};

function mergeMentorshipContent(remoteContent) {
  const safe = remoteContent && typeof remoteContent === 'object' ? remoteContent : {};

  return {
    header: {
      ...DEFAULT_MENTORSHIP_CONTENT.header,
      ...(safe.header || {}),
    },
    hero: {
      ...DEFAULT_MENTORSHIP_CONTENT.hero,
      ...(safe.hero || {}),
    },
    buttons: {
      ...DEFAULT_MENTORSHIP_CONTENT.buttons,
      ...(safe.buttons || {}),
    },
    form: {
      ...DEFAULT_MENTORSHIP_CONTENT.form,
      ...(safe.form || {}),
    },
  };
}

function humanSupabaseError(err) {
  if (!err) return 'Something went wrong. Please try again.';

  const code = String(err.code || '');
  const msg = String(err.message || '');

  if (/row level security/i.test(msg) || /permission/i.test(msg)) {
    return 'Your request was blocked by database security rules. Please contact the administrator.';
  }

  return msg || `Database error${code ? ` (${code})` : ''}. Please try again.`;
}

export default function MentorshipForm() {
  const [activeBrand, setActiveBrand] = useState(PROGRAMMES[0]);
  const [showForm, setShowForm] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [appRow, setAppRow] = useState(null);
  const [bannerError, setBannerError] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(DEFAULT_PAYFAST_AMOUNT);
  const [selectedProgramme, setSelectedProgramme] = useState('Online');
  const [cmsContent, setCmsContent] = useState(DEFAULT_MENTORSHIP_CONTENT);

  const [formData, setFormData] = useState({
    status: '',
    name: '',
    surname: '',
    email: '',
    contact: '',
    gender: '',
    location: ''
  });
  const [errors, setErrors] = useState({});

  const header = cmsContent.header || DEFAULT_MENTORSHIP_CONTENT.header;
  const hero = cmsContent.hero || DEFAULT_MENTORSHIP_CONTENT.hero;
  const buttons = cmsContent.buttons || DEFAULT_MENTORSHIP_CONTENT.buttons;
  const formText = cmsContent.form || DEFAULT_MENTORSHIP_CONTENT.form;

const openFormFor = (amount, programme) => {
  setSelectedAmount(amount);
  setSelectedProgramme(programme);
  setFormData(f => ({ ...f, status: '' }));
  setErrors({});
  setBannerError('');
  setShowForm(true);
};

  useEffect(() => {
    let cancelled = false;

    async function loadMentorshipContent() {
      try {
        if (!supabase || !supabase.from) return;

        const { data, error } = await supabase
          .from('website_pages')
          .select('is_live, published_content')
          .eq('page_key', 'mentorship')
          .single();

        if (error || !data || data.is_live === false) {
          console.warn('Using fallback mentorship content:', error || 'Page offline or missing');
          return;
        }

        if (!cancelled) {
          setCmsContent(mergeMentorshipContent(data.published_content));
        }
      } catch (error) {
        console.warn('Failed to load mentorship CMS content. Using fallback content.', error);
      }
    }

    loadMentorshipContent();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pay = params.get('pay');
    const pid = params.get('pid');

    if (!pid) return;
    if (pay !== 'success' && pay !== 'cancel') return;

    (async () => {
      try {
        if (!supabase || !supabase.from) return;

        const nowIso = new Date().toISOString();

        const updates =
          pay === 'success'
            ? {
              paid: true,
              payment_date: nowIso,
              payfast_token: null,
              payfast_method: null,
              payment_reference: null,
            }
            : {
              paid: false,
              payment_date: nowIso,
              payfast_token: null,
              payfast_method: null,
              payment_reference: null,
            };

        const { error } = await supabase
          .from('mentorship_applications_2026')
          .update(updates)
          .eq('id', pid);

        if (error) {
          console.error('Return/Cancel URL update failed:', error);
          alert(`Payment ${pay} detected, but failed to update database: ${humanSupabaseError(error)}`);
          return;
        }

        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        alert(
          pay === 'success'
            ? 'Payment success detected ✅ Database updated.'
            : 'Payment cancelled ❌ Database updated.'
        );
      } catch (e) {
        console.error('Return/Cancel URL update exception:', e);
        alert(`Payment ${pay} detected, but update crashed. Please contact support.`);
      }
    })();
  }, []);

  const update = (e) => {
    const { name, value, type } = e.target;
    if (type === 'radio') {
      setFormData(f => ({ ...f, [name]: value }));
    } else {
      setFormData(f => ({ ...f, [name]: value }));
    }

    setErrors(prev => {
      if (!prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
    setBannerError('');
  };

  const validateDetails = () => {
    const e = {};
    if (!formData.status) e.status = 'Choose an applicant category';
    if (!formData.name.trim()) e.name = 'Required';
    if (!formData.surname.trim()) e.surname = 'Required';
    if (!formData.email.trim()) e.email = 'Required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email';
    if (!formData.contact.trim()) e.contact = 'Required';
    if (formData.contact && !/^[0-9+\s()-]{7,}$/.test(formData.contact)) e.contact = 'Invalid number';
    if (!formData.gender) e.gender = 'Choose male or female';
    if (!formData.location) e.location = 'Choose a location';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveAndOpenPay = async () => {
    setBannerError('');
    if (!validateDetails()) return;

    setSaving(true);

    try {
      if (!supabase || !supabase.from) {
        console.error('Supabase client not initialized. SUPA_URL or SUPA_KEY may be missing.');
        setBannerError('System error: database connection is not configured. Please contact the administrator.');
        setSaving(false);
        return;
      }

      const normalizedEmail = String(formData.email || '').trim().toLowerCase();

      const payload = {
        status: formData.status,
        name: formData.name,
        surname: formData.surname,
        email: normalizedEmail,
        contact: formData.contact,
        gender: formData.gender,
        location: formData.location,
        programme_type: selectedProgramme,
        notes: programmeNote(activeBrand),
        paid: false,
      };

      console.log('Attempting insert payload:', payload);

      const res = await supabase
        .from('mentorship_applications_2026')
        .insert([payload])
        .select()
        .single();

      console.log('Supabase insert result:', res);

      const data = res?.data ?? null;
      const error = res?.error ?? null;

      if (error) {
        console.error('Insert returned error object:', error);
        try {
          console.error('Raw error full object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
        } catch (jsonErr) { }

        setBannerError(humanSupabaseError(error));
        setSaving(false);
        return;
      }

      console.log('Insert succeeded, data:', data);
      setAppRow(data);
      setShowForm(false);
      setShowPay(true);
      setSaving(false);
    } catch (err) {
      console.error('Unexpected exception during insert:', err);
      setBannerError('Unexpected error saving your application. Please try again, or contact FWIL if the issue persists.');
      setSaving(false);
    }
  };

  const submitPayfast = () => {
    const pf = document.getElementById('payfastForm');
    if (!pf) {
      alert('Payment form missing.');
      return;
    }
    pf.submit();
  };

  return (
    <div className="fwil-page">
      <header className="logo-header">
        <img src={header.logoUrl || fwilLogoPath} alt="For Women in Law" />
      </header>

      <nav className="programme-tabs" aria-label="Mentorship programmes">
        {PROGRAMMES.map(brand => (
          <button key={brand} type="button" className={`programme-tab ${activeBrand === brand ? 'active' : ''}`}
            aria-pressed={activeBrand === brand} onClick={() => setActiveBrand(brand)}>{brand}</button>
        ))}
      </nav>
      <section className="hero dark-bg">
        <div className="hero-inner">
          <div className="hero-left">
            <p className="tiny-tag">{hero.label}</p>
            <h1>{hero.heading}</h1>
            <p className="lede">
              {hero.description}
              <br />
              <br />
              {hero.note}
              <br />
            </p>

            <div className="cta-row">
              <button
                className="btn primary"
                onClick={() => openFormFor(buttons.onlineAmount || '350', 'Online')}
              >
                {buttons.onlineLabel || 'Online'} - R{buttons.onlineAmount || '350'}
              </button>

              {activeBrand === 'FWiL (SA)' && <button
                className="btn primary"
                onClick={() => openFormFor(buttons.hybridAmount || '550', 'Hybrid')}
              >
                {buttons.hybridLabel || 'Hybrid'} - R{buttons.hybridAmount || '550'}
              </button>}
            </div>
          </div>

          <div className="hero-right">
            <div className="hero-card">
              <img className="hero-img" src={hero.heroImage || heroImagePath} alt="FWIL community" />
              <div className="hero-overlay">
                <span className="badge">{hero.badge}</span>
                <p className="caption">{hero.caption}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showForm && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modal-panel wide">
            <div className="modal-head">
              <button className="link-back" onClick={() => setShowForm(false)}>← Back</button>
              <h3>{formText.modalTitle || 'Tell us about you'}</h3>
              <span className="chip">{formData.status || 'Applicant'}</span>
            </div>

            <div className="modal-body">
              {bannerError && (
                <div
                  style={{
                    background: '#fff1f2',
                    border: '1px solid #fecdd3',
                    color: '#9f1239',
                    padding: '10px 12px',
                    borderRadius: 10,
                    marginBottom: 12,
                    fontWeight: 700
                  }}
                >
                  {bannerError}
                </div>
              )}

              <div className="form-grid">
                <div className="form-field full">
                  <label>Are you a</label>
                  <div className="segmented" role="radiogroup" aria-label="Status">
                    <input
                      type="radio"
                      id="status_student"
                      name="status"
                      value="Students & Graduates"
                      checked={formData.status === 'Students & Graduates'}
                      onChange={update}
                    />
                    <label htmlFor="status_student">Students &amp; Graduates</label>

                    <input
                      type="radio"
                      id="status_graduate"
                      name="status"
                      value="Legal Professionals & Pivoting Professionals"
                      checked={formData.status === 'Legal Professionals & Pivoting Professionals'}
                      onChange={update}
                    />
                    <label htmlFor="status_graduate">Legal Professionals &amp; Pivoting Professionals</label>
                  </div>
                  {errors.status && <span className="err">{errors.status}</span>}
                </div>

                <div className="form-field">
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={update} placeholder="e.g., Noemi" />
                  {errors.name && <span className="err">{errors.name}</span>}
                </div>

                <div className="form-field">
                  <label>Surname</label>
                  <input name="surname" value={formData.surname} onChange={update} placeholder="e.g., Muya" />
                  {errors.surname && <span className="err">{errors.surname}</span>}
                </div>

                <div className="form-field">
                  <label>Email address</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={update}
                    placeholder="you@example.com"
                    style={errors.email ? { borderColor: '#e11d48' } : undefined}
                  />
                  {errors.email && <span className="err">{errors.email}</span>}
                </div>

                <div className="form-field">
                  <label>Contact number</label>
                  <input name="contact" value={formData.contact} onChange={update} placeholder="+27 ..." />
                  {errors.contact && <span className="err">{errors.contact}</span>}
                </div>

                <div className="form-field full">
                  <label>Gender</label>
                  <div className="segmented" role="radiogroup" aria-label="Gender">
                    <input
                      type="radio"
                      id="gender_female"
                      name="gender"
                      value="Female"
                      checked={formData.gender === 'Female'}
                      onChange={update}
                    />
                    <label htmlFor="gender_female">Female</label>

                    <input
                      type="radio"
                      id="gender_male"
                      name="gender"
                      value="Male"
                      checked={formData.gender === 'Male'}
                      onChange={update}
                    />
                    <label htmlFor="gender_male">Male</label>
                  </div>
                  {errors.gender && <span className="err">{errors.gender}</span>}
                </div>

                <div className="form-field full">
                  <label>Location</label>
                  <div className="segmented" style={{ gridTemplateColumns: 'auto auto auto' }}>
                    <input type="radio" id="loc_pta" name="location" value="Pretoria" checked={formData.location === 'Pretoria'} onChange={update} />
                    <label htmlFor="loc_pta">Pretoria</label>

                    <input type="radio" id="loc_jhb" name="location" value="Johannesburg" checked={formData.location === 'Johannesburg'} onChange={update} />
                    <label htmlFor="loc_jhb">Johannesburg</label>

                    <input type="radio" id="loc_out" name="location" value="Outside Gauteng" checked={formData.location === 'Outside Gauteng'} onChange={update} />
                    <label htmlFor="loc_out">Outside Gauteng</label>
                  </div>
                  {errors.location && <span className="err">{errors.location}</span>}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn ghost-dark" onClick={() => setShowForm(false)}>Cancel</button>
              <h5>{formText.paymentNote}</h5>
              <button className="btn primary" onClick={saveAndOpenPay} disabled={saving}>
                {saving ? 'Saving...' : `${formText.continuePrefix || 'Continue: once off fee'} R${selectedAmount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPay && appRow && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modal-panel">
            <div className="modal-head">
              <button className="link-back" onClick={() => { setShowPay(false); setShowForm(true); }}>← Back</button>
              <h3>Confirm & Pay</h3>
              <span className="pill">R{selectedAmount}</span>
            </div>

            <div className="modal-body">
              <div className="summary">
                <div className="row"><span className="label">Programme</span><span className="val">{activeBrand} — {selectedProgramme}</span></div>
                <div className="row"><span className="label">Applying as</span><span className="val">{appRow.status}</span></div>
                <div className="row"><span className="label">Name</span><span className="val">{appRow.name} {appRow.surname}</span></div>
                <div className="row"><span className="label">Email</span><span className="val">{appRow.email}</span></div>
                <div className="row"><span className="label">Contact</span><span className="val">{appRow.contact}</span></div>
                <div className="row"><span className="label">Location</span><span className="val">{appRow.location}</span></div>
              </div>

              <form id="payfastForm" action={PAYFAST_URL} method="post" className="hidden-form">
                <input type="hidden" name="merchant_id" value={PAYFAST_MERCHANT_ID} />
                <input type="hidden" name="merchant_key" value={PAYFAST_MERCHANT_KEY} />

                <input type="hidden" name="amount" value={selectedAmount} />
                <input type="hidden" name="item_name" value={`${activeBrand} Mentorship ${selectedProgramme} - R${selectedAmount}`} />

                <input type="hidden" name="m_payment_id" value={String(appRow.id)} />
                <input type="hidden" name="custom_str1" value={String(appRow.id)} />

                <input type="hidden" name="name_first" value={appRow.name} />
                <input type="hidden" name="name_last" value={appRow.surname} />
                <input type="hidden" name="email_address" value={appRow.email} />
                <input type="hidden" name="cell_number" value={appRow.contact} />

                <input
                  type="hidden"
                  name="notify_url"
                  value="https://fwil-mentorship.vercel.app/api/payfast-itn"
                />

                <input
                  type="hidden"
                  name="return_url"
                  value={`${APP_BASE_URL}/api/payment-return?pay=success&pid=${encodeURIComponent(String(appRow.id))}`}
                />

                <input
                  type="hidden"
                  name="cancel_url"
                  value={`${APP_BASE_URL}/api/payment-return?pay=cancel&pid=${encodeURIComponent(String(appRow.id))}`}
                />
              </form>
            </div>

            <div className="modal-actions">
              <button className="btn ghost-dark" onClick={() => { setShowPay(false); setShowForm(true); }}>Cancel</button>
              <button className="btn primary" onClick={submitPayfast}>
                Pay R{selectedAmount}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}